import { useEffect, useRef } from "preact/hooks";

export interface TemplateSuggestion {
  name: string;
  description: string;
  tags?: string[];
  preview_url: string;
}

// The preview iframe renders at this CSS width and is then scaled down to
// whatever the thumbnail box measures. Rendering the page at its natural
// width and shrinking it is what makes a small thumbnail show the whole
// design; sizing the iframe itself to the box would just crop it and hand
// the user two scrollbars over a sliver of the page.
const PREVIEW_WIDTH = 600;

export function TemplateSuggestionCards({
  templates,
  onApply,
}: {
  templates: TemplateSuggestion[];
  onApply: (name: string) => void;
}) {
  return (
    <div class="vstack gap-2 mt-2">
      {templates.map((tpl) => (
        <TemplateSuggestionCard
          key={tpl.name}
          tpl={tpl}
          onApply={() => onApply(tpl.name)}
        />
      ))}
    </div>
  );
}

function TemplateSuggestionCard({
  tpl,
  onApply,
}: {
  tpl: TemplateSuggestion;
  onApply: () => void;
}) {
  const previewRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Measure the thumbnail box and scale the PREVIEW_WIDTH-wide iframe to fit.
  // Same trick as the create-form tiles: a pure-CSS scale(calc(100cqw / 600))
  // is invalid — scale() needs a unitless <number> and cqw math yields a
  // <length>, so browsers drop it.
  useEffect(() => {
    const el = previewRef.current;
    const ifr = iframeRef.current;
    if (!el || !ifr) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w =
          entry.contentBoxSize?.[0]?.inlineSize ?? entry.contentRect.width;
        if (w > 0) ifr.style.transform = `scale(${w / PREVIEW_WIDTH})`;
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <button
      type="button"
      class="tpl-suggestion text-start"
      onClick={onApply}
      title={tpl.description}
    >
      <div ref={previewRef} class="tpl-suggestion-preview">
        <iframe
          ref={iframeRef}
          src={tpl.preview_url}
          title={`Preview of ${tpl.name}`}
          loading="lazy"
          scrolling="no"
        />
      </div>
      <div class="tpl-suggestion-meta">
        <div class="fw-semibold text-truncate">{tpl.name}</div>
        <div class="tpl-suggestion-desc text-body-secondary">
          {tpl.description}
        </div>
        <div class="tpl-suggestion-cta">
          Use this <i class="bi bi-arrow-right"></i>
        </div>
      </div>
    </button>
  );
}
